/**
 * ═══════════════════════════════════════════════════════════════════════════════
 * PARTICLE FIELD — Audio-reactive floating particles
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * Hundreds of small particles float in 2D space. Audio energy affects them:
 *   - Bass energy pushes particles outward from center (explosion effect)
 *   - Treble energy makes particles change color / sparkle
 *   - Beat detection triggers burst effects (spawn extra particles)
 *
 * This visualization teaches how to manage independent objects (particles)
 * that each respond to shared audio data.
 *
 * PARTICLE LIFECYCLE:
 *   1. Particles are spawned with random position, velocity, size, and lifetime.
 *   2. Each frame, particles move, fade, and react to audio.
 *   3. When a particle's lifetime expires, it's recycled (reset to new random state).
 *   4. On beat, we "burst" — give particles extra outward velocity.
 */

import { Visualization, FrameData, VisualizationSetting } from '../../types';
import { BeatPulse } from '../../utils/audioUtils';
import { hsl } from '../../utils/colorUtils';

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  size: number;
  life: number;     // remaining life (0-1, starts at 1)
  maxLife: number;   // total lifetime in ms
  hue: number;
}

export class ParticleField implements Visualization {
  id = 'particle-field';
  name = 'Particle Field';
  category = 'particles' as const;
  description = 'Floating particles that react to bass and treble energy';
  renderer = 'canvas2d' as const;

  private canvas!: HTMLCanvasElement;
  private ctx!: CanvasRenderingContext2D;
  private particles: Particle[] = [];
  private beatPulse = new BeatPulse(0.9);

  private particleCount = 300;
  private baseSpeed = 0.3;
  private colorHue = 260;

  init(canvas: HTMLCanvasElement): void {
    this.canvas = canvas;
    this.ctx = canvas.getContext('2d')!;
    this.spawnParticles();
  }

  private spawnParticles(): void {
    this.particles = [];
    for (let i = 0; i < this.particleCount; i++) {
      this.particles.push(this.createParticle());
    }
  }

  private createParticle(burst = false): Particle {
    const { width, height } = this.canvas;
    const cx = width / 2;
    const cy = height / 2;

    // For burst particles: start from center with high outward velocity
    if (burst) {
      const angle = Math.random() * Math.PI * 2;
      const speed = 1.5 + Math.random() * 3;
      return {
        x: cx + (Math.random() - 0.5) * 40,
        y: cy + (Math.random() - 0.5) * 40,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        size: 1.5 + Math.random() * 2.5,
        life: 1,
        maxLife: 1500 + Math.random() * 2000,
        hue: this.colorHue + Math.random() * 60 - 30,
      };
    }

    // Normal particles: random position, gentle drift
    return {
      x: Math.random() * (width || 800),
      y: Math.random() * (height || 600),
      vx: (Math.random() - 0.5) * this.baseSpeed,
      vy: (Math.random() - 0.5) * this.baseSpeed,
      size: 1 + Math.random() * 2,
      life: Math.random(), // start at random life so they don't all die at once
      maxLife: 3000 + Math.random() * 5000,
      hue: this.colorHue + Math.random() * 60 - 30,
    };
  }

  render(frame: FrameData): void {
    const { bassEnergy, trebleEnergy, isBeat, beatIntensity, deltaTime } = frame;
    const { width, height } = this.canvas;
    const ctx = this.ctx;
    const cx = width / 2;
    const cy = height / 2;

    this.beatPulse.update(isBeat, beatIntensity);

    // Fade to black (slight trail for smooth particle movement)
    ctx.fillStyle = 'rgba(0, 0, 0, 0.9)';
    ctx.fillRect(0, 0, width, height);

    // On beat: give all particles an outward push + spawn burst particles
    if (isBeat) {
      for (const p of this.particles) {
        const dx = p.x - cx;
        const dy = p.y - cy;
        const dist = Math.sqrt(dx * dx + dy * dy) || 1;
        const pushStrength = beatIntensity * 2;
        p.vx += (dx / dist) * pushStrength;
        p.vy += (dy / dist) * pushStrength;
      }
    }

    const dt = deltaTime / 16.67; // normalize to 60fps

    for (const p of this.particles) {
      // Update life
      p.life -= (deltaTime / p.maxLife);

      // Recycle dead particles
      if (p.life <= 0) {
        Object.assign(p, this.createParticle());
        continue;
      }

      // Bass pushes particles outward from center (gentle continuous force)
      const dx = p.x - cx;
      const dy = p.y - cy;
      const dist = Math.sqrt(dx * dx + dy * dy) || 1;
      p.vx += (dx / dist) * bassEnergy * 0.15 * dt;
      p.vy += (dy / dist) * bassEnergy * 0.15 * dt;

      // Apply velocity with damping (friction)
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      p.vx *= 0.98;
      p.vy *= 0.98;

      // Wrap around edges
      if (p.x < -10) p.x = width + 10;
      if (p.x > width + 10) p.x = -10;
      if (p.y < -10) p.y = height + 10;
      if (p.y > height + 10) p.y = -10;

      // ── Draw particle ───────────────────────────────────────────────────
      // Treble makes particles shift hue (sparkle effect)
      const hue = p.hue + trebleEnergy * 80;
      const alpha = p.life * 0.8;
      const size = p.size + this.beatPulse.value * 1.5;

      ctx.beginPath();
      ctx.arc(p.x, p.y, size, 0, Math.PI * 2);
      ctx.fillStyle = hsl(hue, 70, 60, alpha);
      ctx.fill();
    }
  }

  resize(width: number, height: number): void {
    this.canvas.width = width;
    this.canvas.height = height;
    this.spawnParticles();
  }

  destroy(): void {
    this.particles = [];
  }

  getSettings(): VisualizationSetting[] {
    return [
      { key: 'particleCount', label: 'Particles', type: 'range', value: this.particleCount, min: 100, max: 600, step: 50 },
      { key: 'baseSpeed', label: 'Drift Speed', type: 'range', value: this.baseSpeed, min: 0.05, max: 1, step: 0.05 },
      { key: 'colorHue', label: 'Color Shift', type: 'range', value: this.colorHue, min: 0, max: 360, step: 10 },
    ];
  }

  setSettings(settings: Record<string, number | string | boolean>): void {
    if (typeof settings.particleCount === 'number' && settings.particleCount !== this.particleCount) {
      this.particleCount = settings.particleCount;
      this.spawnParticles();
    }
    if (typeof settings.baseSpeed === 'number') this.baseSpeed = settings.baseSpeed;
    if (typeof settings.colorHue === 'number') this.colorHue = settings.colorHue;
  }
}
